import { useMemo } from 'react';

import classNames from 'classnames';

import { TableSkeletonCell } from '@ui/uikit/components/common/Table/TableSkeletonCell';

import { Typography } from '../Typography';
import type { TableData, TableProps } from './Table';
import { TableBodyCell } from './TableBodyCell';
import type { TableRowProps } from './TableRow';
import { TableRow } from './TableRow';

export type MobileLayoutType = {
  isMobileLayout?: boolean;
};

type TableBodyProps<Data extends TableData> = Required<
  Pick<TableProps<Data>, 'columns' | 'data' | 'topColumns' | 'isLoading'>
> &
  Pick<TableRowProps, 'style' | 'hasSidePadding'> &
  MobileLayoutType;

export const TableBody = <Data extends TableData>({
  columns,
  data,
  style,
  isMobileLayout,
  topColumns,
  isLoading,
  hasSidePadding,
}: TableBodyProps<Data>) => {
  const filteredColumns = useMemo(() => {
    return isMobileLayout ? columns.filter((col) => !topColumns.includes(col.name)) : columns;
  }, [columns, isMobileLayout, topColumns]);

  return data.map((row, rowIndex) => (
    <div
      className={classNames(
        'w-full',
        isMobileLayout && 'pt-3 pb-[11px] border-b border-base-border-light',
      )}
      key={`${row.id}-${rowIndex}`}
    >
      {isMobileLayout && (
        <TableRow
          style={style}
          key={`${row.id}-top`}
          hasBorder={false}
          hasSidePadding={hasSidePadding}
        >
          {topColumns.map((topColumnKey, colIndex) => {
            const column = columns.find((col) => col.name === topColumnKey);

            if (!column) {
              return null;
            }

            const { name, label, noRightPadding, render, contentPadding, align } = column;

            const cellContent = render
              ? render({ row, rowIndex, colIndex, name, label })
              : row[name];

            const contentToRender = !isLoading ? cellContent : <TableSkeletonCell />;

            return (
              <TableBodyCell
                key={`${row.id}-col-${colIndex}-top`}
                noRightPadding={noRightPadding}
                contentPadding={contentPadding}
                isMobileLayout={isMobileLayout}
                align={align}
              >
                {contentToRender}
              </TableBodyCell>
            );
          })}
        </TableRow>
      )}

      <TableRow
        key={row.id}
        style={isMobileLayout ? {} : style}
        hasBorder={!isMobileLayout}
        hasSidePadding={hasSidePadding}
      >
        {filteredColumns.map((column, colIndex) => {
          const { name, label, noRightPadding, render, contentPadding, align } = column;
          const isLastColumn = colIndex === columns.length - 1;
          const finalNoRightPadding = isMobileLayout ? true : noRightPadding ?? isLastColumn;

          const cellContent = render ? render({ row, rowIndex, colIndex, name, label }) : row[name];

          const contentToRender = !isLoading ? cellContent : <TableSkeletonCell />;
          const labelToRender = !isLoading ? String(label) : <TableSkeletonCell />;

          return (
            <TableBodyCell
              key={`${row.id}-col-${colIndex}`}
              noRightPadding={finalNoRightPadding}
              contentPadding={contentPadding}
              isMobileLayout={isMobileLayout}
              align={align}
            >
              {isMobileLayout ? (
                <div
                  className={classNames(
                    'flex w-full items-start',
                    label ? 'justify-between' : 'justify-start',
                  )}
                >
                  {label && (
                    <Typography variant="caption-s" color="tertiary" textAlign="left">
                      {labelToRender}
                    </Typography>
                  )}
                  {contentToRender}
                </div>
              ) : (
                contentToRender
              )}
            </TableBodyCell>
          );
        })}
      </TableRow>
    </div>
  ));
};
